/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useRef, useState} from 'react';
import {PageContainer} from 'components/PageContainer/PageContainer';
import {SearchBar} from 'components/SearchBar/SearchBar';
import {Grid} from '@material-ui/core';
import {StatisticCard} from 'components/StatisticCard/StatisticCard';
import {LaboratoryService} from 'services/LaboratoryService';
import {useHistory, useParams} from 'react-router-dom';
import {localize} from 'commons/LocalizedText/LocalizedText';
import {COLUMN_CHART_AXIS_LABEL, DETAILED_STATISTICS} from 'utils/constants/stats';
import ExcelExportButton from 'components/ExcelExportButton/ExcelExportButton';
import {useDialog} from 'hook/DialogProvider';
import {DialogType} from 'commons/Dialog/CustomDialog';
import {convertJsonToXlsx} from 'utils/excelConverter';
import {ProgramService} from 'services/ProgramService';
import {mapStatRecords} from 'utils/mapper/statGridExportMapper';
import {GENERATE_ROUTE, ROUTES} from 'utils/constants/routes';
import {ErrorName} from 'utils/constants/error.name';

export const HomePage = () => {
  const showDialog = useDialog();
  const history = useHistory();
  const [globalStatistics, setGlobalStatistics] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('All');
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const { laboratory: laboratoryId } = useParams();
  const previousController = useRef();

  const getGlobalStats = () => {
    setIsLoadingStats(true);
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    previousController.current = controller;
    LaboratoryService.getLaboratoryGlobalStats(
      laboratoryId,
      {
        program: selectedProgram ?? 'All',
        selectedYears: selectedYears
      },
      signal
    ).then(resp => {
      setGlobalStatistics(resp);
      setIsLoadingStats(false);
    }).catch(errorMessage => {
      if (errorMessage !== ErrorName.ABORT_ERROR) {
        showDialog({
          variant: DialogType.ERROR,
          title: localize('error'),
          description: errorMessage
        });
        history.push(GENERATE_ROUTE(ROUTES.ERROR), { errorMessage: errorMessage });
      }

    });
  }

  useEffect(() => {
    getGlobalStats();
  }, [])

  useEffect(() => {
    getGlobalStats();
  }, [selectedYears, selectedProgram])

  const updateDataSearchBar = (newSelectedProgram, newSelectedYears) => {
    setSelectedProgram(newSelectedProgram);
    setSelectedYears(newSelectedYears);
  }

  const getPieChartStatisticData = statisticTitle => {
    const statisticData = [['', '']];
    DETAILED_STATISTICS[statisticTitle].stats.forEach(stat => {
      statisticData.push({
        name: localize(stat),
        stat: globalStatistics[stat]
      })
    });
    return statisticData;
  }

  const getColumnChartStatisticData = statisticTitle => {
    if(globalStatistics.length === 0) return;
    const statisticData = [['', ...DETAILED_STATISTICS[statisticTitle].stats.map(ds => localize(ds))]];
    COLUMN_CHART_AXIS_LABEL.forEach((label, index) => {
      if(DETAILED_STATISTICS[statisticTitle].stats){
        statisticData.push({
          name: localize(label),
          stat1: globalStatistics[DETAILED_STATISTICS[statisticTitle].stats[0]] !== undefined ?
            globalStatistics[DETAILED_STATISTICS[statisticTitle].stats[0]][index]
            :
            1
          ,
          stat2: globalStatistics[DETAILED_STATISTICS[statisticTitle].stats[1]] !== undefined ?
            globalStatistics[DETAILED_STATISTICS[statisticTitle].stats[1]][index]
            :
            1
        })
      }
    });
    return statisticData;
  }

  const generateGlobalStatsExcel = (promises, statToExport) => {
    const date = new Date();
    const time = date.getTime();
    Promise.all(promises).then(() => {
      const recordsToExport = statToExport
        .sort(sortOn(stat => stat?.program?.toLocaleUpperCase()))
        .map(stat => mapStatRecords(stat));
      convertJsonToXlsx(recordsToExport, `${localize('globalStatisticsExport')}${date.toLocaleDateString()}-${time}`);
      setIsLoadingExport(false);
    }).catch(() => {
      setIsLoadingExport(false);
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: localize('exportError')
      });
    })
  };

  const sort = (a,b) => (
    b < a ?  1
      : b > a ? -1
        : 0
  );
  const sortOn = getProperty => ((a,b) => sort(getProperty(a),getProperty(b)));

  const handleExport = () => {
    setIsLoadingExport(true);
    if(globalStatistics){
      let statToExport = []
      const promises = [];
      if(!selectedProgram){
        ProgramService.getProgramsByLaboratoryId(laboratoryId).then(programs => {
          programs.forEach(program => {
            promises.push(
              LaboratoryService.getLaboratoryGlobalStats(
                laboratoryId,
                {
                  program: program.text,
                  selectedYears: selectedYears
                }
              ).then(resp => {
                statToExport.push({program: program.text, ...resp});
              })
            );
          });
          generateGlobalStatsExcel(promises, statToExport);
        })
      }else{
        promises.push(
          LaboratoryService.getLaboratoryGlobalStats(
            laboratoryId,
            {
              program: selectedProgram,
              selectedYears: selectedYears
            }
          ).then(resp => {
            statToExport.push({program: selectedProgram, ...resp});
          })
        );
        generateGlobalStatsExcel(promises, statToExport);
      }
    }else{
      setIsLoadingExport(false);
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: localize('noDataToExport')
      });
    }
  }

  const displayStatisticCard = stat => {
    const statNumber = stat.stats.reduce((accumulator, statLabel, index) => {
      if(index !== 0){
        return accumulator + globalStatistics[statLabel];
      }else{
        return accumulator + 0;
      }
    }, 0);
    return (
      <StatisticCard
        chartType={stat.hasBoundaries ? "ColumnChart" : "PieChart"}
        isLoadingStats={isLoadingStats}
        stats={{
          statNumber: stat.hasBoundaries ? null : statNumber,
          detailedStats: stat.hasBoundaries ?
            getColumnChartStatisticData(stat.name)
            :
            getPieChartStatisticData(stat.name)
        }}
        title={stat.name}
        totalLabel={stat.name === 'projectedTests' ? 'projectedTestsLabel' : 'orderedTests'}
      />
    )
  }

  return (
    <PageContainer>
      <SearchBar
        handleOnSearch={updateDataSearchBar}
      />
      <br/>
      <Grid container>
        {Object.values(DETAILED_STATISTICS).map(stat =>
          <Grid item key={stat.name} xs={4}>
            {displayStatisticCard(stat)}
          </Grid>
        )}
      </Grid>
      <br/>
      {!isLoadingStats && (
        <ExcelExportButton
          handleExport={handleExport}
          isLoadingExport={isLoadingExport}
        />
      )}
    </PageContainer>
  )
}